import { Button } from '@vrplatform/components';
import { useBilling, useQuery, useTeam } from '@vrplatform/data';
import { EllipsisMenuItem } from '@vrplatform/components-legacy';
import { Avatar, Menu, rem } from '@mantine/core';
import type { connection_bool_exp } from '@vrplatform/graphql';
import { whereConnectionStatusDefault } from '@vrplatform/ui-common';
import {
  type ComponentProps,
  type ReactNode,
  useCallback,
  useState,
} from 'react';
import { SelectConnectionByAppModal } from '../select-connection-by-app-modal';
import { CsvImportModal } from './CsvImportModal';
import type { ConnectionExtractor } from '../extract-single-modal/useExtractSingleButtonQuery';

type Importer = {
  docsLink: string; // 'https://docs.vrplatform.com/integrations/booking-com/payout';
  uniqueRef: string; // payout;
  description: string; // 'Import payout data from Booking.com';
  types?: string[]; // ['payment'];
};

function useImportersQuery(props?: { skip: boolean }) {
  const [{ id: teamId }] = useTeam();

  return useQuery(
    (q, args) => {
      const where: connection_bool_exp = {
        tenantId: { _eq: args.teamId },
        status: whereConnectionStatusDefault,
        app: {
          info: { _has_key: 'importers' },
        },
      };

      return q
        .connection({
          where,
          distinct_on: ['appId'],
        })
        .map((connection) => ({
          id: connection.id,
          app: {
            id: connection.appId,
            name: connection.app.name,
            logo: connection.app.iconRound,
          },
          hasMultipleConnections:
            connection.app
              .connections({
                where,
                limit: 2,
              })
              .map((x) => x.id).length > 1,
          importers: (connection.app.info({
            path: 'importers',
          }) || []) as Importer[],
          extractors: (connection.app.info({
            path: 'extractors',
          }) || []) as ConnectionExtractor[],
        }));
    },
    {
      keepPreviousData: true,
      skip: props?.skip,
      variables: {
        teamId,
      },
      queryKey: ['connections'],
    }
  );
}

type Props = {
  variant?: ComponentProps<typeof Button>['variant'];
  children?: ReactNode;
  size?: ComponentProps<typeof Button>['size'];
  uniqueRef: ComponentProps<typeof CsvImportModal>['uniqueRef'];
  connectionId?: string;
};

type Connection = NonNullable<
  ReturnType<typeof useImportersQuery>['data']
>[number];

type ImportConnection = ComponentProps<typeof CsvImportModal>['connection'];

export const CsvImportButton = ({
  variant = 'transparent',
  children = 'Import',
  uniqueRef,
  size,
  // connectionId: initialConnectionId,
}: Props) => {
  const [importConnection, setImportConnection] = useState<ImportConnection>();
  const [selectConnectionByApp, setSelectConnectionByApp] =
    useState<ComponentProps<typeof SelectConnectionByAppModal>['app']>(null);

  const { isReadOnly } = useBilling();

  const { data: connections, isLoading } = useImportersQuery({
    skip: isReadOnly,
  });

  const connectionsWithUniqueRef = connections
    ?.filter((x) => x.importers.some((y) => y.uniqueRef === uniqueRef))
    .reduce((acc, curr) => {
      if (acc.some((x) => x.app.id === curr.app.id)) {
        return acc;
      }

      acc.push(curr);

      return acc;
    }, [] as Connection[]);

  const hasUniqueRef = !!connectionsWithUniqueRef?.length;
  const isMultipleConnections = (connectionsWithUniqueRef?.length ?? 0) > 1;

  const disabled = !connectionsWithUniqueRef?.length || !hasUniqueRef;

  const handleSelectConnection = useCallback((connection: Connection) => {
    if (connection.hasMultipleConnections) {
      setSelectConnectionByApp({
        id: connection.app.id,
        name: connection.app.name,
        logo: connection.app.logo,
        extractors: connection.extractors,
      });
    } else {
      setImportConnection({
        id: connection.id,
        app: connection.app,
        // documentationUrl: connection.importers.find(
        //   (x) => x.uniqueRef === uniqueRef
        // )?.docsLink,
      });
    }
  }, []);

  if (isReadOnly) return null;

  return (
    <>
      {!isMultipleConnections ? (
        // || initialConnectionId
        <Button
          leftIcon="CloudUploadIcon"
          size={size}
          variant={variant}
          onClick={() => {
            // if (initialConnectionId) {
            //   setImportConnection({
            //     id: initialConnectionId,
            //   });
            //   return;
            // }

            if (connectionsWithUniqueRef?.length === 1) {
              handleSelectConnection(connectionsWithUniqueRef[0]);
            }
          }}
          loading={isLoading}
          disabled={disabled}
        >
          {children}
        </Button>
      ) : (
        <Menu trigger="click" shadow="md" withArrow>
          <Menu.Target>
            <Button
              size={size}
              leftIcon="CloudUploadIcon"
              variant={variant}
              loading={isLoading}
            >
              {children}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {connectionsWithUniqueRef?.map((connection) => (
              <EllipsisMenuItem
                key={connection.id}
                onClick={() => handleSelectConnection(connection)}
                leftSection={
                  <Avatar
                    src={connection.app.logo}
                    size={rem(18)}
                    sx={(theme) => ({
                      border: `1px solid ${theme.colors.gray[2]}`,
                    })}
                  />
                }
              >
                {connection.app.id === 'csvImport' ? (
                  'Import VRPlatform CSV'
                ) : (
                  <>Import from {connection.app.name}</>
                )}
              </EllipsisMenuItem>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}

      <CsvImportModal
        opened={!!importConnection}
        closeModal={() => setImportConnection(undefined)}
        connection={
          // initialConnectionId ||
          importConnection || connectionsWithUniqueRef?.[0]
        }
        uniqueRef={uniqueRef}
      />

      <SelectConnectionByAppModal
        app={selectConnectionByApp}
        closeModal={() => setSelectConnectionByApp(null)}
        onSubmit={setImportConnection}
      />
    </>
  );
};
